<template>
  <v-container fluid>
    <PageTitle icon="fal fa-fw fa-fax">
      Send a Fax
      <template v-slot:buttons>
        <v-btn :to="{name: `${$root.routePrefix}.fax_contacts.index`}"
               class="mr-2">
          Fax Contacts
        </v-btn>
        <v-btn :to="{name: `${$root.routePrefix}.fax_messages.index`}"
               class="mr-2">
          Fax Messages
        </v-btn>
      </template>
    </PageTitle>

    <v-form @submit.prevent="submit">
      <v-card class="mb-4">
        <v-card-text>
          <v-autocomplete v-model="contact"
                          ref="contactCombo"
                          label="Who do you want to send a fax to?"
                          outlined
                          placeholder="Search for your recipient."
                          :search-input.sync="search"
                          :items="contacts"
                          item-value="id"
                          item-text="name_company_phone_status"
                          item-disabled="is_unsubscribed"
                          return-object
                          clearable
                          @change="checkContact">
            <template v-if="contacts.length > 0"
                      v-slot:append-item>
              <v-list-item v-if="search && search.trim()"
                           @click.prevent="addNewContact">
                <strong>"{{ search }}" not found, add new contact</strong>
              </v-list-item>
              <v-list-item v-else
                           @click.prevent="addNewContact">
                <strong>Add new contact</strong>
              </v-list-item>
            </template>
            <template v-else-if="search && search.trim() && contacts.length === 0"
                      v-slot:no-data>
              <v-list-item @click.prevent="addNewContact">
                <strong>"{{ search }}" not found, add new contact</strong>
              </v-list-item>
            </template>
          </v-autocomplete>

          <template v-if="contact">

            <v-text-field v-if="!contact.fax_number"
                          v-model="form.fax_number"
                          label="Fax Number"
                          outlined
                          placeholder="Required"
                          v-mask="'#-(###)-###-####'"
                          required
                          :rules="[rules.required]"
                          :error-messages="form.errors.get('fax_number')">
            </v-text-field>

            <v-autocomplete v-model="form.presentation_id"
                            label="Which file do you want to send?"
                            outlined
                            placeholder="Required"
                            :items="faxablePresentations"
                            item-value="id"
                            :item-text="nameAndPages"
                            item-disabled="is_unsubscribed"
                            required
                            :rules="[rules.required]"
                            :error-messages="form.errors.get('presentation_id')">
            </v-autocomplete>

            <div v-if="thumbnailURL"
                 class="mb-4">
              <v-img :src="thumbnailURL"
                     :alt="presentation.name"
                     width="260"
                     contain
                     style="border: 1px solid #bbbbbb;"/>
            </div>

            <v-select v-if="snippets.length > 0"
                      v-model="form.snippet_id"
                      label="Select a message (Optional)"
                      outlined
                      :items="snippets"
                      item-value="id"
                      item-text="name"
                      :error-messages="form.errors.get('note')"
                      @change="selectSnippet">
              <template v-slot:item="{item: snippet}">
                <div class="d-block py-1">
                  <div class="subtitle-1">
                    {{ snippet.name }}
                  </div>
                  <div v-if="1===2"
                       class="caption"
                       v-html="snippet.html_content">
                  </div>
                </div>
              </template>
            </v-select>

            <div v-if="renderedSnippet">
              <v-textarea label="This message will be printed on the cover page"
                          :value="renderedSnippet"
                          disabled
                          readonly
                          outlined
                          rows="8">
              </v-textarea>
            </div>
          </template>
        </v-card-text>
        <v-card-actions class="px-4">
          <v-btn type="submit"
                 :disabled="!isFormValid"
                 color="primary">
            Send Fax
          </v-btn>
          <v-btn :to="{name: `${$root.routePrefix}.fax_messages.index`}">
            Cancel
          </v-btn>
        </v-card-actions>
        <v-card-text class="pa-0">
          <v-alert type="info"
                   tile
                   close-label="mb-0">
            Faxing is slow. Please allow up to ten minutes for the fax to send. We appreciate your patience.
          </v-alert>
        </v-card-text>
      </v-card>
    </v-form>

    <v-card v-if="previousMessages.length">
      <v-card-title>
        Previous Faxes sent to {{ this.contact.name }}
      </v-card-title>
      <v-card-text>
        Please do not send repeated faxes to the same person or fax number unless you have explicit permission to do so.
      </v-card-text>
      <FaxMessagesPaginatedTable :messages="previousMessages"
                                 :loading="false"
                                 search="">
      </FaxMessagesPaginatedTable>
    </v-card>

    <v-dialog v-model="showContactDialog"
              max-width="850px"
              persistent>
      <create-fax-contact-form @updated="closeContactDialog"
                               @close="closeContactDialog">
      </create-fax-contact-form>
    </v-dialog>
  </v-container>
</template>
<script>
import {mapGetters} from 'vuex';
import pluralize from 'pluralize';
import {Form} from 'vform';
import {find, isObject, orderBy} from 'lodash';
import faxService from '../../../services/faxService';
import formValidation from '../../../plugins/formValidation';
import CreateFaxContactForm from "./components/CreateFaxContactForm";
import {v4 as uuid} from 'uuid';
import DOMPurify from 'dompurify';
import PageTitle from '../../../components/PageTitle.vue';
import FaxMessagesPaginatedTable from './components/FaxMessagesPaginatedTable.vue';

export default {
  components: {FaxMessagesPaginatedTable, PageTitle, CreateFaxContactForm},
  mixins: [formValidation],
  data() {
    return {
      contact: null,
      contacts: [],
      form: new Form({
        fax_contact_id: '',
        fax_number: '',
        notes: '',
        snippet_id: '',
        message_uuid: uuid(),
      }),
      renderedSnippet: '', // MLD-3985
      search: '',
      showContactDialog: false,
      snippets: [],
      previousMessages: [], // MLD-6860
    }
  },
  computed: {
    ...mapGetters([
      'team',
      'user',
    ]),
    ...mapGetters('content', [
      'faxablePresentations',
    ]),
    isFormValid() {
      return this.form.fax_contact_id
        && this.form.presentation_id
        // && this.form.notes // MLD-6673 I made this optional.
        && this.form.message_uuid;
    },
    contactID() {
      if (this.$route.query.contact_id) {
        return parseInt(this.$route.query.contact_id);
      }
      return null;
    },
    presentationID() {
      if (this.$route.query.presentation_id) {
        return parseInt(this.$route.query.presentation_id);
      }
      return null;
    },
    presentation() {
      if (this.form.presentation_id) {
        return find(this.faxablePresentations, p => p.id === this.form.presentation_id);
      }
      return null;
    },
    thumbnailURL() {
      if (this.presentation && this.presentation.thumbnail && this.presentation.thumbnail.url) {
        return this.presentation.thumbnail.url;
      }
      return null;
    },
    pagesCount() {
      if (this.presentation) {
        return this.presentation.num_pages + 1;
      }
      return 0;
    },
    snippet() { // MLD-3983
      if (this.form.snippet_id) {
        return find(this.snippets, s => s.id === this.form.snippet_id);
      }
      return null;
    }
  },
  created() {
    this.fetchData();
  },
  methods: {
    fetchData() {
      faxService.getActiveFaxSnippets()
        .then(data => {
          this.snippets = orderBy(data, ['slug', 'created_at'], ['asc', 'asc']);
        })
        .catch(() => {

        })
      faxService.getContacts()
        .then(data => {
          this.contacts = data;
          if (this.contactID) {
            this.form.fax_contact_id = this.contactID;
            this.contact = find(this.contacts, c => c.id === this.contactID);
          }
          if (this.presentationID) {
            this.form.presentation_id = this.presentationID;
          }
        })
    },
    async fetchPreviousFaxes() { // MLD-6860
      if (this.form.fax_contact_id) {
        const params = {
          fax_contact_id: this.form.fax_contact_id,
          user_id: this.user.id,
        }
        this.previousMessages = await faxService.getMessages(params);
      } else {
        this.previousMessages = [];
      }
    },
    addNewContact() {
      this.$refs.contactCombo.blur();
      this.openContactDialog();
    },
    async checkContact() {
      if (isObject(this.contact)) {
        this.form.fax_contact_id = this.contact.id;
        this.form.fax_number = this.contact.fax_number; // MLD-3988
      } else {
        this.contact = null;
        this.form.fax_contact_id = '';
        this.form.fax_number = null; // MLD-3988
      }
      await this.fetchPreviousFaxes();
    },
    nameAndPages(presentation) {
      return `${presentation.name} - ${pluralize('page', presentation.num_pages, true)}, plus a cover page`;
    },
    selectSnippet() { // MLD-3963
      const snippet = find(this.snippets, s => s.id === this.form.snippet_id);
      if (snippet) {
        this.form.notes = snippet.content;
        this.renderSnippet();
      }
    },
    async submit() {
      if (this.form.busy) {
        return;
      }
      if (!this.isFormValid) {
        return;
      }
      this.selectSnippet(); // MLD-3963
      try {
        let contact = this.contact;
        //console.log('contact', contact);
        this.form.fax_contact_id = contact.id;
        this.form.post(`/api/fax/send`)
          .then(() => {
            this.$router.push({name: `${this.$root.routePrefix}.fax_messages.index`});
          })
          .catch(err => {
            this.$toast.error(`Could not send the fax: ${DOMPurify.sanitize(err.response.data.message)}`);
          })
      } catch (err) {
        this.$toast.error(DOMPurify.sanitize(err.message));
      }
    },
    openContactDialog() {
      this.showContactDialog = true;
    },
    closeContactDialog(contact) {
      this.showContactDialog = false;
      if (contact) {
        this.contact = contact;
        this.checkContact();
      }
      this.fetchData();
    },
    renderSnippet() {
      this.renderedSnippet = null;
      if (this.form.snippet_id && this.form.fax_contact_id && this.form.message_uuid) {
        return faxService.renderSnippet(this.form.snippet_id, this.form.fax_contact_id, this.form.message_uuid)
          .then(data => {
            this.renderedSnippet = data.text;
          })
      }
    }
  }
}
</script>
